import { List, useTable } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { Conference } from '@viq-admin/types';
import { restoreConference } from '../../api/conferences';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import {
  conferenceCopy,
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — an inline literal here would bust
 * DataTable's `effective` memo (and, through it, `prepared`/`finalColumns`)
 * on every render for no reason. See DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * All-tenant list of archived conferences (the `?archived=true` view driven by
 * `meta.archived`).
 *
 * Row click opens the same `/conferences/:id` detail page as the active list —
 * ConferenceShow is archived-aware and swaps Archive for Restore in its header
 * (see conferences/show.tsx). Restore also lives in the row kebab for speed.
 */
export function ConferenceArchivedList() {
  const { tableProps } = useTable<Conference>({
    resource: 'conferences',
    meta: { archived: true },
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  // Scope-aware: reset the table's sticky rows on a tenant switch (see the
  // active list). The archived view narrows by tenant the same way.
  const { companyId: scope } = useCompanyScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const columns: DataColumn<Conference>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 260 },
    { dataIndex: 'location', key: 'location', title: 'Location', kind: 'text', width: 200 },
    { dataIndex: 'startDate', key: 'startDate', title: 'Starts', kind: 'date' },
    { dataIndex: 'archivedAt', key: 'archivedAt', title: 'Archived', kind: 'date' },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: Conference) => (
        <RowActionsMenu
          actions={[
            {
              key: 'restore',
              label: 'Restore',
              cap: 'manage:Conference',
              onRun: async () => {
                await restoreConference(record.id);
                invalidate({ resource: 'conferences', invalidates: ['list'] });
              },
              confirm: {
                title: `Restore ${record.name}?`,
                content: conferenceCopy.restoreBody,
                okText: 'Restore',
                successMessage: `Restored ${record.name}.`,
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <List headerButtons={() => <Link to="/conferences">← Back to active</Link>}>
      <DataTable<Conference>
        tableProps={tableProps}
        columns={columns}
        rowKey="id"
        storageKey="conferencesArchivedList"
        resetKey={scope}
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['name', 'location']}
        searchPlaceholder="Search archived conferences"
        onRowClick={(record) => navigate(`/conferences/${record.id}`)}
        empty={{ title: 'No archived conferences' }}
      />
    </List>
  );
}
